import React, { useEffect, useState } from "react";
import { Menu } from "@headlessui/react";
import { HiOutlinePlus } from "react-icons/hi";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../../components/Tenant/DataTableComponent/DataTableComponent";
import ModalComponent from "../../../../components/Tenant/ModalComponent/ModalComponent";
import { CircleLoaderComponent } from "../../../../components/Loader/CircleLoaderComponent";
import {
  useAddBranchMutation,
  useFetchBranchListMutation,
  useUpdateBranchMutation,
  useUploadBranchCsvMutation,
} from "../../../../features/tenant/branch/branchApi";
import { downloadSampleCSV } from "../../../../utils/downloadSampleCsv";
import { parseCSV } from "../../../../utils/parseCsv";
import { downloadJsonAsCsv } from "../../../../utils/downloadJsonAsCsv";

const initialFilters = { branch_code: "", branch_name: "" };
const initialForm = { id: null, branch_code: "", branch_name: "", location_count: 0 };
const branchCsvTemplate = [
  { name: "branch_code", label: "Sample Branch Code", maxLength: 255 },
  { name: "branch_name", label: "Sample Branch Name", maxLength: 255 },
];

const BranchList = ({ embedded = false }) => {
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);
  const [branchList, setBranchList] = useState([]);
  const [total, setTotal] = useState(0);
  const [showFormModal, setShowFormModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [addTab, setAddTab] = useState("manual");
  const [csvFile, setCsvFile] = useState(null);

  const [fetchBranchList, { isLoading: fetchBranchListIsLoading }] = useFetchBranchListMutation();
  const [addBranch, { isLoading: addBranchIsLoading }] = useAddBranchMutation();
  const [updateBranch, { isLoading: updateBranchIsLoading }] = useUpdateBranchMutation();
  const [uploadBranchCsv, { isLoading: csvUploading }] = useUploadBranchCsvMutation();
  const [fetchBranchListForExport, { isLoading: exportLoading }] = useFetchBranchListMutation();

  const isEdit = Boolean(form.id);
  const isSaving = addBranchIsLoading || updateBranchIsLoading;

  const loadBranches = async (appliedFilters = filters, pageLimit = limit, pageOffset = offset) => {
    try {
      const res = await fetchBranchList({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        body: { ...appliedFilters, limit: pageLimit, offset: pageOffset },
      }).unwrap();
      setBranchList(res?.body?.rows || []);
      setTotal(res?.body?.total || 0);
    } catch (error) {
      toast.error(error?.data?.message || "Error fetching branch list");
    }
  };

  useEffect(() => {
    loadBranches();
  }, [limit, offset]);

  const handlePageChange = (pageNumber) => {
    setOffset((pageNumber - 1) * limit);
    setCurrentPage(pageNumber);
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    if (offset === 0) {
      loadBranches(filters, limit, 0);
    } else {
      setOffset(0);
    }
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
    if (offset === 0) {
      loadBranches(initialFilters, limit, 0);
    } else {
      setOffset(0);
    }
  };

  const openAddModal = () => {
    setForm(initialForm);
    setAddTab("manual");
    setCsvFile(null);
    setShowFormModal(true);
  };

  const openEditModal = (row) => {
    setForm({
      id: row.id,
      branch_code: row.branch_code || "",
      branch_name: row.branch_name || "",
      location_count: row.location_count || 0,
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setForm(initialForm);
    setAddTab("manual");
    setCsvFile(null);
  };

  const handleCsvSubmit = () => {
    if (!csvFile) {
      toast.error("Please select a file");
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = async (event) => {
      const rows = parseCSV(event.target.result);
      try {
        const res = await uploadBranchCsv({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
          body: { rows },
        }).unwrap();
        const insertedCount = res?.body?.inserted?.length || 0;
        const skippedCount = res?.body?.skipped?.length || 0;
        toast.success(`${insertedCount} branch(es) added, ${skippedCount} skipped`);
        closeFormModal();
        loadBranches();
      } catch (error) {
        toast.error(error?.data?.message || "Error uploading branch CSV");
      }
    };
    fileReader.readAsText(csvFile);
  };

  const handleExportCsv = async () => {
    try {
      const res = await fetchBranchListForExport({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        body: { limit: 100000, offset: 0 },
      }).unwrap();
      const rows = res?.body?.rows || [];
      if (rows.length === 0) {
        toast.error("No branches to export");
        return;
      }
      downloadJsonAsCsv(rows, "branch_master.csv");
    } catch (error) {
      toast.error(error?.data?.message || "Error exporting branch CSV");
    }
  };

  const handleSave = async () => {
    const branchCode = form.branch_code.trim();
    const branchName = form.branch_name.trim();

    if (!branchCode || !branchName) {
      toast.error("Branch code and branch name are required");
      return;
    }

    const params = {
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: isEdit
        ? { id: form.id, branch_code: branchCode, branch_name: branchName }
        : { branch_code: branchCode, branch_name: branchName },
    };

    try {
      if (isEdit) {
        await updateBranch(params).unwrap();
        toast.success("Branch updated successfully");
      } else {
        await addBranch(params).unwrap();
        toast.success("Branch added successfully");
      }
      closeFormModal();
      loadBranches();
    } catch (error) {
      toast.error(error?.data?.message || (isEdit ? "Error updating branch" : "Error adding branch"));
    }
  };

  const columns = [
    { name: "SNo", cell: (row, idx) => offset + Number(idx + 1), width: "80px" },
    { name: "Branch Code", selector: (row) => row.branch_code || "-" },
    { name: "Branch Name", selector: (row) => row.branch_name || "-" },
    { name: "Zone Locations", selector: (row) => row.location_count ?? 0 },
    {
      name: "",
      cell: (row) => (
        <button
          onClick={() => openEditModal(row)}
          className="flex justify-center items-center rounded px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition duration-150 ease-in-out"
          style={{ backgroundColor: primaryColor?.["700"] || "#2563eb" }}
        >
          Edit
        </button>
      ),
    },
  ];

  const content = (
    <div className="p-3">
      <div className="p-3 w-full bg-white shadow rounded-lg">
        <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
          <div className="flex space-x-2 justify-end">
            {[
              { key: "branch_code", label: "Branch Code" },
              { key: "branch_name", label: "Branch Name" },
            ].map((item) => (
              <div className="w-52 h-10" key={item.key}>
                <div className="relative searchin">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    {item.label}
                  </label>
                  <input
                    className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    name={item.label}
                    value={filters[item.key]}
                    onChange={(e) => setFilters({ ...filters, [item.key]: e.target.value })}
                  />
                </div>
              </div>
            ))}

            <div className="flex flex-row gap-2 h-10 mt-6">
              <button className="btn btn-outline-primary p-2" onClick={handleSearch}>
                Search
              </button>
              <button className="btn btn-outline-primary p-2" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <DataTableComponent
          keyField="id"
          columns={columns}
          data={branchList}
          totalRows={total}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          paginationDefaultPage={currentPage}
        />

        <div className="fixed bottom-0 right-10 w-16 h-16 mr-10 mb-6 cursor-pointer" id="box_btn">
          <div className="text-right">
            <Menu className="relative inline-block text-left">
              <div>
                <Menu.Button
                  onClick={openAddModal}
                  className="inline-flex justify-cente w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                  style={{ backgroundColor: `${primaryColor["700"]}` }}
                >
                  <HiOutlinePlus className="w-full h-full p-3" />
                </Menu.Button>
              </div>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {(fetchBranchListIsLoading || isSaving || exportLoading) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <CircleLoaderComponent />
        </div>
      )}

      {embedded ? (
        content
      ) : (
        <div className="flex w-full bg-gray-200 h-full justify-center">
          <div className="w-full">
            <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
              Branch List
              <LinksComponent />
            </div>
            {content}
          </div>
        </div>
      )}

      {showFormModal && (
        <ModalComponent show={showFormModal} setShow={closeFormModal} size="md">
          <div className="p-6 flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">
              {isEdit ? "Edit Branch" : "Add Branch"}
            </h2>

            {!isEdit && (
              <div className="flex justify-center gap-2 border-b">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "manual"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("manual")}
                >
                  Add Manually
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "csv"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("csv")}
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "export"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("export")}
                >
                  Export CSV
                </button>
              </div>
            )}

            {isEdit || addTab === "manual" ? (
              <>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Branch Code</label>
                    <input
                      type="text"
                      maxLength={255}
                      value={form.branch_code}
                      onChange={(e) => setForm({ ...form, branch_code: e.target.value })}
                      placeholder="Enter branch code"
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Branch Name</label>
                    <input
                      type="text"
                      maxLength={255}
                      value={form.branch_name}
                      onChange={(e) => setForm({ ...form, branch_name: e.target.value })}
                      placeholder="Enter branch name"
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {isEdit && form.location_count > 0 && (
                    <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-md p-2">
                      This branch is used by {form.location_count} zone location(s). Changes apply to all of them.
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button
                    onClick={closeFormModal}
                    className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !form.branch_code.trim() || !form.branch_name.trim()}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      isSaving || !form.branch_code.trim() || !form.branch_name.trim()
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isEdit ? "Update" : "Add"}
                  </button>
                </div>
              </>
            ) : addTab === "csv" ? (
              <div className="flex flex-col gap-3">
                <div className="flex justify-center">
                  <button
                    type="button"
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                    onClick={() => downloadSampleCSV(branchCsvTemplate)}
                  >
                    Sample CSV Download
                  </button>
                </div>

                <label
                  htmlFor="branch-csv-dropzone"
                  className="flex flex-col items-center justify-center w-full h-20 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <p className="text-sm text-gray-500 text-center">
                    <span className="font-semibold">Choose CSV file</span>
                    <div>Click to upload or drag and drop</div>
                  </p>
                  <input
                    id="branch-csv-dropzone"
                    type="file"
                    className="hidden"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                  />
                </label>
                {csvFile && <p className="text-xs text-gray-500 text-center">{csvFile.name}</p>}

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button
                    onClick={closeFormModal}
                    className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCsvSubmit}
                    disabled={csvUploading || !csvFile}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      csvUploading || !csvFile ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {csvUploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-gray-600">Download every branch currently in the system as a CSV file.</p>
                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button
                    onClick={closeFormModal}
                    className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExportCsv}
                    disabled={exportLoading}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      exportLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {exportLoading ? "Exporting..." : "Download CSV"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default BranchList;
